import React, { useState, useEffect } from 'react'
import { 
  Box, 
  VStack, 
  HStack, 
  Text, 
  Button, 
  Input, 
  FormControl, 
  FormLabel, 
  Badge, 
  Alert, 
  AlertIcon, 
  Container, 
  Grid, 
  GridItem, 
  SimpleGrid,
  Modal, 
  ModalOverlay, 
  ModalContent, 
  ModalHeader, 
  ModalBody, 
  ModalFooter, 
  useToast,
  useDisclosure
} from '@chakra-ui/react'
import { FaArrowLeft, FaSignOutAlt, FaEdit, FaSave, FaTimes } from 'react-icons/fa'
import { useAuthContext } from '@/contexts/AuthContext'
import { usePaywallContext } from '@/contexts/PaywallContext'
import { useNavigate } from 'react-router-dom'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { subscriptionCancellationService } from '@/services/firestore'
import { InvoiceModal } from '@/components/modals/InvoiceModal'
import { SubscriptionPlan } from '@/types/user'
import { AnalyticsService } from '@/services/analytics'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/config/firebase'
import Advertising from '@/components/Advertising'

export const ProfilePage: React.FC = () => {
  const { user, updateProfile, loading, error, clearError, signOut, sendPasswordReset } = useAuthContext()
  const { userSubscription, trialDaysRemaining, isTrialExpired, refreshSubscription } = usePaywallContext()
  const navigate = useNavigate()
  const toast = useToast()
  const { isOpen: isCancelModalOpen, onOpen: onCancelModalOpen, onClose: onCancelModalClose } = useDisclosure()
  const { isOpen: isCancelTrialModalOpen, onOpen: onCancelTrialModalOpen, onClose: onCancelTrialModalClose } = useDisclosure()
  const { isOpen: isInvoiceModalOpen, onOpen: onInvoiceModalOpen, onClose: onInvoiceModalClose } = useDisclosure()
  
  const [isEditing, setIsEditing] = useState(false)
  const [invoices, setInvoices] = useState<any[]>([])
  const [isLoadingInvoices, setIsLoadingInvoices] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null)
  const [adminSettings, setAdminSettings] = useState<any>(null)
  const [pricingPlans, setPricingPlans] = useState<SubscriptionPlan[]>([])
  const [paymentFailureStatus, setPaymentFailureStatus] = useState<any>(null)
  const [notifications, setNotifications] = useState<any[]>([])
  const [paystackSubscriptionData, setPaystackSubscriptionData] = useState<any>(null)
  const [isLoadingPaystackData, setIsLoadingPaystackData] = useState(false)
  const [passwordResetCooldown, setPasswordResetCooldown] = useState(0)
  const [formData, setFormData] = useState({
    displayName: '',
    phoneNumber: '',
    companyName: '',
    vatNumber: '',
    address: {
      street: '',
      city: '',
      postalCode: '',
      country: 'ZA' as const
    }
  })

  // Function to get plan name from planType
  const getPlanDisplayName = (planType: string): string => {
    if (!planType) return 'Unknown Plan'
    
    const plan = pricingPlans.find(p => p.type === planType)
    if (plan) {
      return plan.name || `${planType === 'individual' ? 'Individual' : 'Club'} Plan`
    }
    
    // Fallback to readable names
    return planType === 'individual' ? 'Individual Plan' : 
           planType === 'club' ? 'Club Plan' : 
           planType
  }

  // Fetch pricing plans
  useEffect(() => {
    const fetchPricingPlans = async () => {
      try {
        const { pricingService } = await import('@/services/firestore')
        const plans = await pricingService.getPricingPlans()
        setPricingPlans(plans)
      } catch (error) {
        console.error('Error fetching pricing plans:', error)
        setPricingPlans([])
      }
    }

    fetchPricingPlans()
  }, [])

  // Check payment failure status and notifications
  useEffect(() => {
    const checkPaymentStatus = async () => {
      try {
        const { subscriptionService } = await import('@/services/firestore')
        const paymentStatus = await subscriptionService.checkPaymentFailureStatus()
        setPaymentFailureStatus(paymentStatus)
        
        // Try to get notifications, but don't fail if permissions are missing
        try {
        const notifications = await subscriptionService.getUserNotifications()
        setNotifications(notifications)
        } catch (notificationsError) {
          console.warn('Could not load notifications (permissions issue):', notificationsError)
          setNotifications([])
        }
      } catch (error) {
        console.error('Error checking payment status:', error)
      }
    }

    checkPaymentStatus()
  }, [])

  // Password reset cooldown timer
  useEffect(() => {
    if (passwordResetCooldown > 0) {
      const timer = setTimeout(() => {
        setPasswordResetCooldown(prev => prev - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [passwordResetCooldown])

  // Fetch Paystack subscription data
  useEffect(() => {
    const fetchPaystackData = async () => {
      if (!userSubscription?.paystackSubscriptionId || !user?.uid) return
      
      setIsLoadingPaystackData(true)
      try {
        const { subscriptionService } = await import('@/services/firestore')
        const result = await subscriptionService.checkPaystackSubscription(
          user.uid, 
          userSubscription.paystackSubscriptionId
        )
        
        if (result.subscriptionData) {
           // Handle nested data structure from Paystack API
           const actualData = result.subscriptionData.data || result.subscriptionData
           setPaystackSubscriptionData(actualData)
        }
      } catch (error) {
        console.error('Error fetching Paystack subscription data:', error)
      } finally {
        setIsLoadingPaystackData(false)
      }
    }

    fetchPaystackData()
  }, [userSubscription?.paystackSubscriptionId, user?.uid])

  useEffect(() => {
    if (user) {
      setFormData({
        displayName: user.displayName || '',
        phoneNumber: user.phoneNumber || '',
        companyName: user.companyName || '',
        vatNumber: user.vatNumber || '',
        address: {
          street: user.address?.street || '',
          city: user.address?.city || '',
          postalCode: user.address?.postalCode || '',
          country: 'ZA'
        }
      })
    }
  }, [user])

  // Fetch invoices
  useEffect(() => {
    const fetchInvoices = async () => {
      if (!user?.uid) return
      
      setIsLoadingInvoices(true)
      try {
        const { paymentService } = await import('@/services/firestore')
        const userPayments = await paymentService.getUserPayments(user.uid)
        setInvoices(userPayments)
      } catch (error) {
        console.error('Error fetching invoices:', error)
        setInvoices([])
      } finally {
        setIsLoadingInvoices(false)
      }
    }

    fetchInvoices()
  }, [user?.uid])

  // Fetch admin settings for invoice details
  useEffect(() => {
    const fetchAdminSettings = async () => {
      try {
        const { adminService } = await import('@/services/firestore')
        const settings = await adminService.getAdminSettings()
        setAdminSettings(settings)
      } catch (error) {
        console.error('Error fetching admin settings:', error)
      }
    }

    fetchAdminSettings()
  }, [])

  if (loading) {
    return <LoadingSpinner />
  }

  if (!user) {
    navigate('/auth')
    return null
  }

  const handleSave = async () => {
    try {
      await updateProfile(formData)
      setIsEditing(false)
      clearError()
      toast({
        title: 'Profile Updated',
        description: 'Your profile has been successfully updated.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: 'Failed to update profile. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleCancel = () => {
    setFormData({
      displayName: user.displayName || '',
      phoneNumber: user.phoneNumber || '',
      companyName: user.companyName || '',
      vatNumber: user.vatNumber || '',
      address: {
        street: user.address?.street || '',
        city: user.address?.city || '',
        postalCode: user.address?.postalCode || '',
        country: 'ZA'
      }
    })
    setIsEditing(false)
    clearError()
  }

  const handlePasswordReset = async () => {
    if (passwordResetCooldown > 0) return
    
    if (!user?.email) {
      toast({
        title: 'Email Required',
        description: 'Email address is required to reset password.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      })
      return
    }

    // Set cooldown immediately to prevent multiple clicks
    setPasswordResetCooldown(10)

    try {
      await sendPasswordReset(user.email)
      toast({
        title: 'Password Reset Email Sent',
        description: `We've sent a password reset link to ${user.email}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
    } catch (error) {
      toast({
        title: 'Password Reset Failed',
        description: 'Failed to send password reset email. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleCancelSubscription = async () => {
    try {
      if (!userSubscription || !userSubscription.paystackSubscriptionId) {
        toast({
          title: 'No active subscription',
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
        return
      }

      await subscriptionCancellationService.cancelSubscription(userSubscription.paystackSubscriptionId)
      
      toast({
        title: 'Subscription Cancelled',
        description: 'Your subscription has been cancelled. You will retain access until the end of your billing period.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      
      // Refresh subscription status
      await refreshSubscription()
      onCancelModalClose()
    } catch (error) {
      toast({
        title: 'Cancellation Failed',
        description: 'There was an error cancelling your subscription. Please try again or contact support.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleManageSubscription = async () => {
    try {
      if (!userSubscription?.paystackSubscriptionId) {
        toast({
          title: 'No active subscription',
          description: 'No subscription found to manage.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
        return
      }

      setIsLoadingPaystackData(true)



      // Call Cloud Function to get Paystack subscription management URL
      const getSubscriptionManagementUrl = httpsCallable(functions, 'getSubscriptionManagementUrl')
      const result = await getSubscriptionManagementUrl({
        subscriptionId: userSubscription.paystackSubscriptionId
      })

      const { managementUrl } = result.data as { managementUrl: string }

      if (managementUrl) {
        // Open the Paystack management URL in a new tab
        window.open(managementUrl, '_blank', 'noopener,noreferrer')
        
        toast({
          title: 'Subscription Management',
          description: 'Opening Paystack subscription management page in a new tab.',
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
      } else {
        throw new Error('No management URL received')
      }
    } catch (error) {
      console.error('Error getting subscription management URL:', error)
      toast({
        title: 'Error',
        description: 'Failed to open subscription management. Please try again or contact support.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    } finally {
      setIsLoadingPaystackData(false)
    }
  }

  const handleCancelTrial = async () => {
    try {
      if (!userSubscription) {
        toast({
          title: 'No trial subscription found',
          status: 'error',
          duration: 4000,
          isClosable: true,
        })
        return
      }

      // Track cancellation attempt
      await AnalyticsService.logEvent('subscription_cancellation_attempted', {
        plan_type: userSubscription.planType,
        amount: userSubscription.pricePerMonth,
        currency: 'ZAR',
        subscription_status: userSubscription.status
      });

      // For trial users who verified their card, they DO have a Paystack subscription
      // (13-month subscription with R1.50 first month to simulate trial)
      if (userSubscription.paystackSubscriptionId) {
        // Cancel the Paystack subscription first
        await subscriptionCancellationService.cancelSubscription(userSubscription.paystackSubscriptionId)
      }

      // Then update the local subscription status
      const { subscriptionService } = await import('@/services/firestore')
      const { getAuth } = await import('firebase/auth')
      const auth = getAuth()
      
      if (!auth.currentUser) {
        throw new Error('User not authenticated')
      }
      
      // Update the subscription status to cancelled using userId
      await subscriptionService.updateSubscriptionByUserId(auth.currentUser.uid, {
        status: 'cancelled',
        cancelledAt: new Date() as any,
        autoRenew: false
      })
      
      // Track successful cancellation
      await AnalyticsService.logEvent('subscription_cancellation_completed', {
        plan_type: userSubscription.planType,
        amount: userSubscription.pricePerMonth,
        currency: 'ZAR'
      });
      
      toast({
        title: 'Trial Cancelled',
        description: 'Your trial has been cancelled. You will retain access until the end of your trial period.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      })
      
      // Refresh subscription status
      await refreshSubscription()
      onCancelTrialModalClose()
    } catch (error) {
      // Track cancellation error
      if (userSubscription) {
        await AnalyticsService.logError('subscription_cancellation_failed', error instanceof Error ? error.message : 'Unknown error');
        await AnalyticsService.logEvent('subscription_cancellation_failed', {
          plan_type: userSubscription.planType,
          amount: userSubscription.pricePerMonth,
          currency: 'ZAR',
          error_message: error instanceof Error ? error.message : 'Unknown error'
        });
      }
      
      toast({
        title: 'Cancellation Failed',
        description: 'There was an error cancelling your trial. Please try again or contact support.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      })
    }
  }

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const handleViewInvoice = (invoice: any) => {
    setSelectedInvoice(invoice)
    onInvoiceModalOpen()
  }

  const handleMarkNotificationAsRead = async (notificationId: string) => {
    try {
      const { subscriptionService } = await import('@/services/firestore')
      await subscriptionService.markNotificationAsRead(notificationId)
      
      // Update local notifications
      setNotifications(prev => prev.map(notification => 
        notification.id === notificationId 
          ? { ...notification, read: true }
          : notification
      ))
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }
  }

  const formatDate = (date: any) => {
    if (!date) return 'N/A'
    const d = date.toDate ? date.toDate() : new Date(date)
    return d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })
  }

  const formatDateTime = (date: any) => {
    if (!date) return 'N/A'
    const d = date.toDate ? date.toDate() : new Date(date)
    return `${d.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    })} at ${d.toLocaleTimeString()}`
  }

  return (
    <Box minH="100vh" bg="#09090b" color="white">
      {/* Header */}
      <Box
        position="fixed"
        top={0}
        left={0}
        right={0}
        zIndex={1000}
        bg="#191919"
        borderBottom="1px solid"
        borderColor="gray.700"
        px={6}
        py={2}
      >
        <HStack justify="space-between">
          <HStack spacing={4}>
            <Button
              aria-label="Back to home"
              leftIcon={<FaArrowLeft />}
              onClick={() => navigate('/')}
              variant="ghost"
              color="white"
              _hover={{ bg: 'rgba(255, 255, 255, 0.1)' }}
            />
            <VStack align="start" spacing={0}>
              <Text size="lg" color="white">
                Account
              </Text>
              <Text fontSize="sm" color="gray.400">
                Profile / Billing
              </Text>
            </VStack>
          </HStack>
          <Button
            leftIcon={<FaSignOutAlt />}
            variant="ghost"
            color="red.400"
            _hover={{ bg: 'rgba(239, 68, 68, 0.1)' }}
            onClick={handleSignOut}
          >
            Log Out
          </Button>
        </HStack>
      </Box>

      {/* Edit Profile Button */}
      <Box px={6} py={4} pt={20}>
        <HStack justify="center">
          {isEditing ? (
            <HStack spacing={2}>
              <Button
                leftIcon={<FaTimes />}
                variant="ghost"
                color="gray.400"
                _hover={{ color: 'white' }}
                onClick={handleCancel}
              >
                Cancel
              </Button>
              <Button
                leftIcon={<FaSave />}
                colorScheme="blue"
                onClick={handleSave}
              >
                Save Changes
              </Button>
            </HStack>
          ) : (
            <Button
              leftIcon={<FaEdit />}
              bg="transparent"
              color="blue.400"
              border="1px solid"
              borderColor="blue.400"
              _hover={{ bg: 'rgba(66, 153, 225, 0.1)' }}
              onClick={() => setIsEditing(true)}
            >
              Edit Profile
            </Button>
          )}
        </HStack>
      </Box>

      <Container maxW="container.lg" py={8}>
        <VStack spacing={8} align="stretch">
          {error && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              {error}
            </Alert>
          )}

          {/* No active subscription message */}
          {!userSubscription && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
                              No active subscription.
            </Alert>
          )}

          {/* Club Logo Section - Only show for club plans */}
          {userSubscription?.planType === 'club' && (
            <Box bg="#191919" border="1px solid" borderColor="gray.700">
              <Box p={4} borderBottom="1px solid" borderColor="gray.700">
                <Text size="md" color="blue.400">
                  Advertising Banner / Logo
                </Text>
                <Text fontSize="sm" color="gray.400" mt={1}>
                  Up to 3 uploads (1600 x 200). 3min rotations. Avoid bright{' '}
                  <Text as="span" color="orange.400">colours</Text>.
                </Text>
                <Text fontSize="sm" color="gray.400">
                  PNG, JPG, or SVG (max 2MB each)
                </Text>
                <Text fontSize="sm" color="gray.400" fontStyle="italic">
                  Note: On the scoring screen adjust size by clicking on the image
                </Text>
              </Box>
              <Box p={6}>
                <Advertising />
              </Box>
            </Box>
          )}

          {/* Basic Information */}
          <Box bg="#191919" border="1px solid" borderColor="gray.700">
            <Box p={4} borderBottom="1px solid" borderColor="gray.700">
                             <Text size="md" color="blue.400">
                 Basic Information
               </Text>
            </Box>
            <Box p={6}>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300" mb={2}>Email</FormLabel>
                  <Input
                    value={user.email}
                    disabled
                    bg="#2a2a2a"
                    borderColor="gray.600"
                    color="white"
                    _disabled={{ opacity: 0.7 }}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                     {user.emailVerified ? '✓ Verified' : '⚠ Not verified (please log out and log in again)'}
                  </Text>
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300" mb={2}>Full Name</FormLabel>
                  <Input
                    value={formData.displayName}
                    onChange={(e) => setFormData(prev => ({ ...prev, displayName: e.target.value }))}
                    disabled={!isEditing}
                    bg="#2a2a2a"
                    borderColor="gray.600"
                    color="white"
                    _focus={{ borderColor: 'white', boxShadow: 'none' }}
                    _disabled={{ opacity: 0.7 }}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300" mb={2}>Phone Number</FormLabel>
                  <Input
                    value={formData.phoneNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                    disabled={!isEditing}
                    bg="#2a2a2a"
                    borderColor="gray.600"
                    color="white"
                    _focus={{ borderColor: 'white', boxShadow: 'none' }}
                    _disabled={{ opacity: 0.7 }}
                    placeholder="+27 12 345 6789"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300" mb={2}>Password</FormLabel>
                  <Button
                    onClick={handlePasswordReset}
                    bg="transparent"
                    color={passwordResetCooldown > 0 ? "gray.600" : "gray.400"}
                    border="1px solid"
                    borderColor="gray.600"
                    _hover={{ bg: passwordResetCooldown > 0 ? 'transparent' : 'rgba(113, 128, 150, 0.1)' }}
                    w="full"
                    h="40px"
                    isDisabled={passwordResetCooldown > 0}
                    cursor={passwordResetCooldown > 0 ? 'not-allowed' : 'pointer'}
                  >
                    {passwordResetCooldown > 0 ? `Reset My Password (${passwordResetCooldown}s)` : 'Reset My Password'}
                  </Button>
                </FormControl>
              </Grid>
            </Box>
          </Box>

          {/* VAT Information */}
          <Box bg="#191919" border="1px solid" borderColor="gray.700">
            <Box p={4} borderBottom="1px solid" borderColor="gray.700">
                             <Text size="md" color="blue.400">
                 VAT Information (Optional)
               </Text>
            </Box>
            <Box p={6}>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300" mb={2}>Company Name</FormLabel>
                  <Input
                    value={formData.companyName}
                    onChange={(e) => setFormData(prev => ({ ...prev, companyName: e.target.value }))}
                    disabled={!isEditing}
                    bg="#2a2a2a"
                    borderColor="gray.600"
                    color="white"
                    _focus={{ borderColor: 'white', boxShadow: 'none' }}
                    _disabled={{ opacity: 0.7 }}
                    placeholder="Company name"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300" mb={2}>VAT Number</FormLabel>
                  <Input
                    value={formData.vatNumber}
                    onChange={(e) => setFormData(prev => ({ ...prev, vatNumber: e.target.value }))}
                    disabled={!isEditing}
                    bg="#2a2a2a"
                    borderColor="gray.600"
                    color="white"
                    _focus={{ borderColor: 'white', boxShadow: 'none' }}
                    _disabled={{ opacity: 0.7 }}
                    placeholder="VAT registration number"
                  />
                </FormControl>
              </Grid>
            </Box>
          </Box>

          {/* Address */}
          <Box bg="#191919" border="1px solid" borderColor="gray.700">
            <Box p={4} borderBottom="1px solid" borderColor="gray.700">
                             <Text size="md" color="blue.400">
                 Address
               </Text>
            </Box>
            <Box p={6}>
              <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                <GridItem colSpan={{ base: 1, md: 2 }}>
                  <FormControl>
                    <FormLabel fontSize="sm" color="gray.300" mb={2}>Street Address</FormLabel>
                    <Input
                      value={formData.address.street}
                      onChange={(e) => setFormData(prev => ({ 
                        ...prev, 
                        address: { ...prev.address, street: e.target.value }
                      }))}
                      disabled={!isEditing}
                      bg="#2a2a2a"
                      borderColor="gray.600"
                      color="white"
                      _focus={{ borderColor: 'white', boxShadow: 'none' }}
                      _disabled={{ opacity: 0.7 }}
                      placeholder="Street address"
                    />
                  </FormControl>
                </GridItem>

                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300" mb={2}>City</FormLabel>
                  <Input
                    value={formData.address.city}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, city: e.target.value }
                    }))}
                    disabled={!isEditing}
                    bg="#2a2a2a"
                    borderColor="gray.600"
                    color="white"
                    _focus={{ borderColor: 'white', boxShadow: 'none' }}
                    _disabled={{ opacity: 0.7 }}
                    placeholder="City"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel fontSize="sm" color="gray.300" mb={2}>Postal Code</FormLabel>
                  <Input
                    value={formData.address.postalCode}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      address: { ...prev.address, postalCode: e.target.value }
                    }))}
                    disabled={!isEditing}
                    bg="#2a2a2a"
                    borderColor="gray.600"
                    color="white"
                    _focus={{ borderColor: 'white', boxShadow: 'none' }}
                    _disabled={{ opacity: 0.7 }}
                    placeholder="Postal code"
                  />
                </FormControl>
              </Grid>
            </Box>
          </Box>

                                {/* Trial Status - Show for active trials (either no subscription or trial subscription) */}
           {!isTrialExpired && (!userSubscription || userSubscription.status === 'trial') && (
             <Box bg="#191919" border="1px solid" borderColor="gray.700">
              <Box p={4} borderBottom="1px solid" borderColor="gray.700">
                               <Text size="md" color="blue.400">
                 Trial Status
               </Text>
              </Box>
              <Box p={6}>
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                                     <Box>
                     <Text fontSize="sm" color="gray.300" mb={2}>Status</Text>
                     <Badge
                       colorScheme={!isTrialExpired ? 'green' : 'red'}
                       fontSize="sm"
                       px={3}
                       py={1}
                     >
                       {!isTrialExpired ? 'Active Trial' : 'Trial Expired'}
                     </Badge>
                   </Box>

                   {!isTrialExpired && trialDaysRemaining !== null && (
                     <Box>
                       <Text fontSize="sm" color="gray.300" mb={2}>Days Remaining</Text>
                       <Text fontSize="lg" fontWeight="bold" color="white">
                         {trialDaysRemaining} days
                       </Text>
                     </Box>
                   )}

                   {user.trial?.trial_ends && (
                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <Text fontSize="sm" color="gray.300" mb={2}>Trial Ends</Text>
                      <Text fontSize="sm" color="gray.400">
                         {formatDate(user.trial.trial_ends)}
                      </Text>
                    </GridItem>
                  )}

                  {user.trial?.planName && (
                   <GridItem colSpan={{ base: 1, md: 2 }}>
                     <Text fontSize="sm" color="gray.300" mb={2}>Desired Plan</Text>
                     <Text fontSize="sm" color="gray.400">
                       {user.trial.planName}
                     </Text>
                   </GridItem>
                 )}

                 {/* Card Verification Skipped Alert */}
                 {user.trial?.skippedVerification && (
                   <GridItem colSpan={{ base: 1, md: 2 }}>
                      <Alert status="warning" borderRadius="md" bg="yellow.100">
                       <AlertIcon />
                       <Box flex="1">
                          <Text fontWeight="bold" color="black">Card Verification Skipped</Text>
                          <Text fontSize="sm" color="black">
                           You skipped card verification during signup. To continue after your trial ends, you'll need to provide payment details.
                         </Text>
                       </Box>
                     </Alert>
                   </GridItem>
                 )}
                </Grid>
              </Box>
            </Box>
          )}

                     {/* Manage Subscription */}
           {userSubscription && userSubscription.paystackSubscriptionId && (
             <Box bg="#191919" border="1px solid" borderColor="gray.700">
               <Box p={4} borderBottom="1px solid" borderColor="gray.700">
                 <Text size="md" color="blue.400">
                   Subscription Management
                 </Text>
               </Box>
               <Box p={6}>
                 <VStack spacing={4} align="stretch">
                   {/* Subscription Status */}
                   <Box>
                     <HStack justify="space-between" align="center" mb={2}>
                       <Text fontSize="sm" color="gray.300">Status</Text>
                       <Button
                         size="xs"
                         onClick={() => window.location.reload()}
                         colorScheme="blue"
                         variant="outline"
                       >
                         Refresh
                       </Button>
                     </HStack>
                     <Badge
                       colorScheme={
                         userSubscription.status === 'active' ? 'green' :
                         userSubscription.status === 'trial' ? 'yellow' :
                         userSubscription.status === 'cancelled' || userSubscription.status === 'non-renewing' ? 'red' : 'gray'
                       }
                       fontSize="sm"
                       px={3}
                       py={1}
                     >
                       {userSubscription.status === 'active' ? 'Active' :
                        userSubscription.status === 'trial' ? 'Trial' :
                        userSubscription.status === 'cancelled' || userSubscription.status === 'non-renewing' ? 'Cancelled' :
                        userSubscription.status}
                     </Badge>
                   </Box>

                   {/* Subscription Details */}
                   {isLoadingPaystackData ? (
                     <Text color="gray.400">Loading subscription details...</Text>
                   ) : (
                     <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={6}>
                       <Box>
                         <Text fontSize="sm" color="gray.300" mb={2}>Next Payment Date</Text>
                         <Text fontSize="lg" fontWeight="bold" color="white">
                           {userSubscription.status === 'cancelled' || userSubscription.status === 'non-renewing' ? 'N/A (Cancelled)' :
                            paystackSubscriptionData?.next_payment_date 
                             ? formatDate(new Date(paystackSubscriptionData.next_payment_date))
                             : userSubscription.currentPeriodEnd 
                               ? formatDate(userSubscription.currentPeriodEnd)
                               : userSubscription.trialEndsAt 
                                 ? formatDate(userSubscription.trialEndsAt)
                                 : 'N/A'
                           }
                         </Text>
                       </Box>

                       <Box>
                         <Text fontSize="sm" color="gray.300" mb={2}>Amount</Text>
                         <Text fontSize="lg" fontWeight="bold" color="white">
                           {paystackSubscriptionData?.amount 
                             ? `R${(paystackSubscriptionData.amount / 100).toFixed(2)}`
                             : userSubscription.pricePerMonth 
                               ? `R${(userSubscription.pricePerMonth / 100).toFixed(2)}`
                               : 'R33.15'
                           }
                         </Text>
                       </Box>

                       {paystackSubscriptionData?.plan?.name && (
                         <Box gridColumn={{ base: 1, md: 'span 2' }}>
                           <Text fontSize="sm" color="gray.300" mb={2}>Plan</Text>
                           <Text fontSize="sm" color="gray.400">
                             {paystackSubscriptionData.plan.name}
                           </Text>
                         </Box>
                       )}

                       {/* Cancellation Date */}
                       {userSubscription.cancelledAt && (
                         <Box gridColumn={{ base: 1, md: 'span 2' }}>
                           <Text fontSize="sm" color="gray.300" mb={2}>Cancelled On</Text>
                           <Text fontSize="sm" color="red.400">
                             {formatDate(userSubscription.cancelledAt)}
                           </Text>
                         </Box>
                       )}

                       {/* Plan Change Information */}
                       <Box gridColumn={{ base: 1, md: 'span 2' }} mt={6} p={4} bg="gray.800" borderRadius="md" border="1px solid" borderColor="gray.600">
                         <Text fontSize="sm" color="gray.300">
                           {userSubscription.status === 'cancelled' || userSubscription.status === 'non-renewing'
                             ? 'Your subscription has been cancelled. You can resubscribe to any plan to regain access.'
                             : <>To <Text as="span" color="green.400">change your Subscription Plan</Text> please <Text as="span" color="red.400">cancel</Text> your existing subscription first and then sign up to the plan of your choice.</>
                           }
                         </Text>
                       </Box>
                     </Grid>
                   )}
                   
                   {userSubscription.status !== 'cancelled' && userSubscription.status !== 'non-renewing' && (
                     <>
                       <Text fontSize="sm" color="gray.300">
                         Update payment details, or cancel your subscription directly through Paystack:
                       </Text>
                       <Button
                         colorScheme="blue"
                         onClick={handleManageSubscription}
                         isLoading={isLoadingPaystackData}
                         loadingText="Loading..."
                       >
                         Manage Subscription
                       </Button>
                     </>
                   )}
                   
                   {(userSubscription.status === 'cancelled' || userSubscription.status === 'non-renewing') && (
                     <>
                       <Text fontSize="sm" color="gray.300">
                         Your subscription has been cancelled. To regain access to all features:
                       </Text>
                       <Button
                         colorScheme="blue"
                         onClick={() => navigate('/pricing')}
                       >
                         Resubscribe
                       </Button>
                     </>
                   )}
                 </VStack>
              </Box>
            </Box>
          )}

          {/* Payment Failure Alert */}
          {paymentFailureStatus?.hasPaymentIssues && (
            <Alert status="error" borderRadius="md">
              <AlertIcon />
              <Box flex="1">
                <Text fontWeight="bold">Payment Failed</Text>
                <Text fontSize="sm">
                  Your subscription payment failed. Reason: {paymentFailureStatus.lastFailureReason || 'Unknown error'}. 
                  Please update your payment method using the "Manage Subscription" button above.
                </Text>
              </Box>
            </Alert>
          )}

          {/* Notifications */}
          {notifications.length > 0 && (
            <Box bg="#191919" border="1px solid" borderColor="gray.700">
              <Box p={4} borderBottom="1px solid" borderColor="gray.700">
                <Text size="md" color="blue.400">
                  Notifications
                </Text>
              </Box>
              <Box p={6}>
                <VStack spacing={3} align="stretch">
                  {notifications.map((notification) => (
                    <Box
                      key={notification.id}
                      p={3}
                      bg={notification.read ? "#2a2a2a" : "#1a1a1a"}
                      border="1px solid"
                      borderColor={notification.read ? "gray.600" : "blue.400"}
                      borderRadius="md"
                      cursor={notification.read ? "default" : "pointer"}
                      onClick={() => !notification.read && handleMarkNotificationAsRead(notification.id)}
                    >
                      <HStack justify="space-between" align="start">
                        <Box flex="1">
                          <Text fontWeight="bold" color="white" fontSize="sm">
                            {notification.title}
                          </Text>
                          <Text color="gray.400" fontSize="xs" mt={1}>
                            {notification.message}
                          </Text>
                          <Text color="gray.500" fontSize="xs" mt={1}>
                            {formatDateTime(notification.createdAt)}
                          </Text>
                        </Box>
                        {!notification.read && (
                          <Badge colorScheme="blue" fontSize="xs">
                            New
                          </Badge>
                        )}
                      </HStack>
                    </Box>
                  ))}
                </VStack>
              </Box>
            </Box>
          )}

          



          

          {/* Invoices Section - Only show for club plans */}
          {userSubscription?.planType === 'club' && (
            <Box bg="#191919" border="1px solid" borderColor="gray.700">
              <Box p={4} borderBottom="1px solid" borderColor="gray.700">
                               <Text size="md" color="blue.400">
                   Invoices
                 </Text>
              </Box>
              <Box p={6}>
                {isLoadingInvoices ? (
                  <LoadingSpinner message="Loading invoices..." />
                ) : invoices.length === 0 ? (
                  <Text color="gray.400">No invoices found.</Text>
                ) : (
                  <VStack spacing={2} align="stretch">
                    {invoices.map((invoice) => (
                      <HStack
                        key={invoice.id}
                        bg="#2a2a2a"
                        border="1px solid"
                        borderColor="gray.600"
                        borderRadius="md"
                        p={3}
                        justify="space-between"
                        align="center"
                      >
                        <Text fontSize="sm" color="gray.300">
                          {formatDate(invoice.createdAt)}
                        </Text>
                        <Text fontSize="sm" color="gray.400">
                          R{(invoice.amount / 100).toFixed(2)}
                        </Text>
                        <Button
                          size="xs"
                          onClick={() => handleViewInvoice(invoice)}
                          colorScheme="blue"
                          variant="outline"
                        >
                          View
                        </Button>
                      </HStack>
                    ))}
                  </VStack>
                )}
              </Box>
            </Box>
          )}


        </VStack>
      </Container>

      {/* Cancel Subscription Modal */}
      <Modal isOpen={isCancelModalOpen} onClose={onCancelModalClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="#191919" color="white">
          <ModalHeader>Confirm Cancellation</ModalHeader>
          <ModalBody>
            <Text>
              Are you sure you want to cancel your subscription? You will retain access until the end of your billing period.
            </Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onCancelModalClose} mr={3}>
              Back
            </Button>
            <Button colorScheme="red" onClick={handleCancelSubscription}>
              Yes, Cancel Subscription
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

             {/* Cancel Trial Modal */}
       <Modal isOpen={isCancelTrialModalOpen} onClose={onCancelTrialModalClose} isCentered>
         <ModalOverlay />
         <ModalContent bg="#191919" color="white">
           <ModalHeader>Confirm Trial Cancellation</ModalHeader>
           <ModalBody>
             <Text>
               Are you sure you want to cancel?
             </Text>
           </ModalBody>
           <ModalFooter>
             <Button onClick={onCancelTrialModalClose} mr={3}>
               Back
             </Button>
             <Button colorScheme="red" onClick={handleCancelTrial}>
               Yes, Cancel Trial
             </Button>
           </ModalFooter>
         </ModalContent>
       </Modal>


      {/* Invoice Modal */}
      <InvoiceModal
        isOpen={isInvoiceModalOpen}
        onClose={onInvoiceModalClose}
        invoice={selectedInvoice}
        adminSettings={adminSettings}
        currentUser={user}
      />
    </Box>
  )
} 